import type { CreateSetup } from '@hvf/contracts'
import { type FormStore, getValues, setValue } from '@modular-forms/solid'
import { calculateValues } from './utils/calculateValues'
import { findMiddle } from './utils/findMiddle'
import { getpatternSpecification } from './utils/getpatternSpecification'
import { isSetupLogScale } from './utils/isSetupLogScale'

// Helper to avoid infinite reactivity loops by only updating if the value actually changed
const setIfChanged = (form: FormStore<CreateSetup, undefined>, field: keyof CreateSetup, newValue: any) => {
  if (getValues(form)[field] !== newValue) {
    setValue(form, field, newValue as any)
  }
}

export const updateComputedFields = (form: FormStore<CreateSetup, undefined>) => {
  const formValues = getValues(form)

  // need the direction to be either set manually, or derived from the difference between the time.
  if (formValues.position === undefined) {
    return
  }

  const isLog = isSetupLogScale(formValues as CreateSetup)
  const isLong = formValues.position === 'long'

  const values = [
    formValues.high_1_price,
    formValues.low_1_price,
    formValues.high_2_price,
    formValues.low_2_price,
    formValues.high_3_price,
    formValues.low_3_price,
  ]

  // check that every value is a number and greater than 0
  if (values.some((v) => v === null || v === undefined || Number.isNaN(v) || v <= 0)) {
    // If the technical values are invalid, we MUST clear the computed fields
    // so they don't stay stuck on the screen with stale calculations
    setIfChanged(form, 'axis', undefined)
    setIfChanged(form, 'risk_reward_ratio', undefined)
    setIfChanged(form, 'interim_1', undefined)
    setIfChanged(form, 'interim_2', undefined)
    setIfChanged(form, 'target', undefined)
    setIfChanged(form, 'pattern_specification', undefined)
    return
  }

  const axis = findMiddle(formValues.high_3_price!, formValues.low_3_price!)
  const patternSpecification = getpatternSpecification(
    formValues.high_1_price!,
    formValues.low_1_price!,
    axis,
    isLong,
  )

  const { target, interim2, interim1, riskRewardRatio } = calculateValues(
    formValues.high_1_price!,
    formValues.high_2_price!,
    formValues.high_3_price!,
    formValues.low_1_price!,
    formValues.low_2_price!,
    formValues.low_3_price!,
    axis,
    isLong,
    isLog,
  )

  setIfChanged(form, 'axis', axis)
  setIfChanged(form, 'risk_reward_ratio', Math.round(riskRewardRatio * 100) / 100)
  setIfChanged(form, 'interim_1', interim1)
  setIfChanged(form, 'interim_2', interim2)
  setIfChanged(form, 'target', target)
  setIfChanged(form, 'pattern_specification', patternSpecification)
}
